import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { Box, useMediaQuery } from '@mui/material';
import { useParams } from 'react-router-dom';
import { addDays, startOfToday, startOfTomorrow } from 'date-fns';
import { isAfter, isBefore } from 'date-fns/fp';
import MobileDrawer from 'feature/dispatching/components/MobileDrawer';
import AddItemButton from 'feature/dispatching/components/AddItemButton';
import { useUserRolePermissions } from 'hooks';
import DriverCard from './DriverCard';
import DriverCardSkeleton from './DriverCardSkeleton';
import DriverFilters from './DriverFilters';
import LocationHeader from './LocationHeader';
import {
  useViewDriverDetailsByLocationQuery,
  useViewDriverSummaryByLocationQuery,
} from '../driverStatusEndpoints';
import AddDriversToLocationWizard from '../add-drivers-to-location-wizard/AddDriversToLocationsWizard';

const showV2Features = process.env.REACT_APP_DISPATCHING_VERSION >= '2.0.0';

const containerStyles = {
  /* eslint-disable-next-line */
  bgcolor: 'surface.light',
  mt: { md: 6, xs: 0 },
  pb: { md: 0, xs: 8 },
  pl: { md: 37.5, xs: 1 },
  pr: { md: 0, xs: 1 },
};

const boxStyles = {
  display: 'grid',
  gap: 3,
  gridTemplateColumns: `repeat(auto-fit, 300px)`,
  ml: 3,
};

const ViewDriverDetailsByLocation = ({ onCreate, onRemove, onView }) => {
  const { isDispatchingManage } = useUserRolePermissions();
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { viewById } = useParams();
  const locationId = parseInt(viewById);

  const defaultState = {
    capability: 'All',
    driver: '',
    expiration: 'Any',
    status: 'All',
  };

  const [cardsFilters, setCardsFilters] = useState(defaultState);

  const {
    data = [],
    isLoading,
    isSuccess,
  } = useViewDriverDetailsByLocationQuery({ locationId }, { skip: R.isNil(viewById) });

  const { location: locationQuery = {} } = useViewDriverSummaryByLocationQuery(undefined, {
    selectFromResult: result => ({
      location: result.data?.find(R.propEq('locationId', locationId)),
    }),
  });

  // prettier-ignore
  const driverFilter = R.compose(
    R.includes(cardsFilters.driver?.toUpperCase()),
    R.converge(R.concat, [
      R.o(R.toUpper, R.prop('firstName')),
      R.o(R.toUpper, R.prop('lastName'))
    ]),
  )

  const statusFilter = R.compose(
    isActive =>
      R.or(
        cardsFilters.status === 'All',
        R.or(
          R.and(cardsFilters.status === 'Active', isActive),
          R.and(cardsFilters.status === 'Inactive', !isActive)
        )
      ),
    R.prop('isActive')
  );

  // expiration filters
  const validFilter = R.both(
    R.always(R.equals(cardsFilters.expiration, 'Valid')),
    isAfter(startOfToday())
  );
  const expiredFilter = R.both(
    R.always(R.equals(cardsFilters.expiration, 'Expired')),
    isBefore(startOfTomorrow())
  );
  const almostExpiredFilter = R.both(
    R.always(R.equals(cardsFilters.expiration, '30 days')),
    R.both(isAfter(startOfToday()), isBefore(addDays(startOfToday(), 30)))
  );
  const expirationFilter = R.ifElse(
    R.always(R.equals(cardsFilters.expiration, 'Any')),
    R.T,
    R.compose(
      R.ifElse(
        R.isNil,
        R.F,
        // prettier-ignore
        R.compose(
          R.anyPass([validFilter, expiredFilter, almostExpiredFilter]), 
          d => new Date(d)
        )
      ),
      R.prop('expirationDate')
    )
  );

  // capability filters
  const loadFilter = R.compose(
    R.and(R.equals('Load', cardsFilters.capability)),
    R.includes(R.__, ['LOAD & UNLOAD', 'LOAD ONLY']),
    R.prop('loadCapabilities')
  );
  const unloadFilter = R.compose(
    R.and(R.equals('Unload', cardsFilters.capability)),
    R.includes(R.__, ['LOAD & UNLOAD', 'UNLOAD ONLY']),
    R.prop('loadCapabilities')
  );
  const capabilityFilter = R.anyPass([
    R.always(R.equals(cardsFilters.capability, 'All')),
    loadFilter,
    unloadFilter,
  ]);

  const filteredDrivers = data
    .filter(driverFilter)
    .filter(statusFilter)
    .filter(expirationFilter)
    .filter(capabilityFilter);

  const handleResetFilters = () => {
    setCardsFilters(defaultState);
  };

  const handleRemoveFilter = byLabel => {
    setCardsFilters(current => R.assoc(byLabel, defaultState[byLabel], current));
  };

  const filtersPanel = (
    <DriverFilters
      cardsFilters={cardsFilters}
      onResetFilters={handleResetFilters}
      setCardsFilters={setCardsFilters}
    />
  );

  const [addDriverOpened, openAddDriver] = useState(false);
  const handleAddDriverOpen = R.compose(openAddDriver, R.T);
  const handleAddDriverClose = R.compose(openAddDriver, R.F);

  // prettier-ignore
  // renderBadges :: [String] -> String
  const renderBadges = R.compose(
    R.when(
      xs => R.gt(R.length(xs), 20), 
      R.o(R.concat(R.__, '...'), R.take(17))
    ),
    R.join(', ')
  );

  const renderContent = () => (
    <Box sx={boxStyles}>
      {isLoading && (
        <>
          <DriverCardSkeleton />
          <DriverCardSkeleton />
          <DriverCardSkeleton />
        </>
      )}

      {showV2Features && isDispatchingManage && isSuccess && (
        <AddItemButton itemName="Driver" onAdd={handleAddDriverOpen} />
      )}

      {isSuccess &&
        filteredDrivers.map(driver => (
          <DriverCard
            driver={driver}
            key={driver.driverId}
            location={locationQuery}
            onRemove={onRemove}
            onView={onView}
            subheader={renderBadges(driver.badgeIds)}
            title={
              !R.isEmpty(filteredDrivers)
                ? `${R.toUpper(driver.lastName)}, ${R.toUpper(driver.firstName)}`
                : ''
            }
          />
        ))}
      {addDriverOpened && (
        <AddDriversToLocationWizard onClose={handleAddDriverClose} openDrawer={addDriverOpened} />
      )}
    </Box>
  );

  return lgUp ? (
    <Box sx={containerStyles}>
      <LocationHeader
        cardsFilters={cardsFilters}
        defaultFiltersState={defaultState}
        filtersPanel={filtersPanel}
        onCreate={onCreate}
        onRemoveFilter={handleRemoveFilter}
      />
      {renderContent()}
    </Box>
  ) : (
    <MobileDrawer
      backLabel="Back to Locations"
      createLabel="New Driver"
      filtersPanel={filtersPanel}
      module="compliance/driver-status"
      open={!!viewById}
      setTitle={() => {}}
    >
      {renderContent()}
    </MobileDrawer>
  );
};

ViewDriverDetailsByLocation.propTypes = {
  onCreate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
};

export default ViewDriverDetailsByLocation;
